import { useEffect, useMemo, useState } from "react";
import { ExternalLink, FolderOpen, HardDrive, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";

export default function WeeklyBackups({ canManage = false }) {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/service-audit/backups/settings");
            setProjects((data.projects || []).map((project) => ({
                ...project,
                enabled: Boolean(project.enabled),
                drive_folder_url: project.drive_folder_url || "",
            })));
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const updateProject = (projectId, patch) => {
        if (!canManage) return;
        setProjects((current) => current.map((project) => project.project_id === projectId ? { ...project, ...patch } : project));
    };

    const save = async () => {
        if (!canManage) return;
        setSaving(true);
        try {
            const { data } = await api.patch("/service-audit/backups/settings", {
                root_drive_url: null,
                projects: projects.map((project) => ({
                    project_id: project.project_id,
                    enabled: Boolean(project.enabled),
                    drive_folder_url: project.drive_folder_url?.trim() || null,
                })),
            });
            setProjects((data.projects || []).map((project) => ({ ...project, drive_folder_url: project.drive_folder_url || "" })));
            toast.success("Configuración de backups guardada");
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const active = useMemo(() => projects.filter((project) => project.enabled).length, [projects]);
    const configured = useMemo(() => projects.filter((project) => project.drive_folder_url).length, [projects]);

    return (
        <div className="space-y-4">
            <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <div className="flex items-center gap-2"><HardDrive className="h-5 w-5 text-orange-500" /><h3 className="font-semibold text-slate-900">BackUps semanales</h3></div>
                    <p className="mt-1 text-sm text-slate-500">Configura qué proyectos requieren backup y la carpeta de Google Drive asociada a cada uno.</p>
                    <p className="mt-2 text-xs text-slate-400">{active} proyectos incluidos · {configured} carpetas configuradas</p>
                </div>
                {canManage && <Button onClick={save} disabled={saving}><Save className="mr-2 h-4 w-4" />{saving ? "Guardando…" : "Guardar cambios"}</Button>}
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="max-h-[58vh] overflow-y-auto">
                    {loading ? (
                        <div className="py-16 text-center text-slate-400">Cargando proyectos…</div>
                    ) : projects.length ? (
                        <div className="divide-y divide-slate-100">
                            {projects.map((project) => (
                                <div key={project.project_id} className="grid gap-3 p-4 lg:grid-cols-[auto_minmax(220px,1fr)_minmax(320px,1.5fr)] lg:items-center">
                                    <Checkbox disabled={!canManage} checked={project.enabled} onCheckedChange={(value) => updateProject(project.project_id, { enabled: Boolean(value) })} aria-label={`Activar backup ${project.project_name}`} />
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-[11px] font-semibold text-orange-600">{project.deployment_code}</span>
                                            {project.production_url && <a href={project.production_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"><ExternalLink className="h-3 w-3" />Producción</a>}
                                        </div>
                                        <p className="mt-1 truncate text-sm font-semibold text-slate-900">{project.project_name}</p>
                                        <p className="truncate text-xs text-slate-500">{project.client_name}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <FolderOpen className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                            <Input disabled={!canManage} className="pl-9" value={project.drive_folder_url} onChange={(event) => updateProject(project.project_id, { drive_folder_url: event.target.value })} placeholder="https://drive.google.com/drive/folders/..." />
                                        </div>
                                        {project.drive_folder_url?.trim() && <a href={project.drive_folder_url.trim()} target="_blank" rel="noreferrer" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50" title="Abrir carpeta"><ExternalLink className="h-4 w-4" /></a>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="py-16 text-center text-slate-400">No hay proyectos activos para configurar backups.</div>
                    )}
                </div>
            </div>
        </div>
    );
}
